import { selectBestCoupon } from '../utils/coupons';
import { getVendorWebsiteLinkText } from '../utils/vendorWebsiteLink';
import { trackVendorClick } from '../services/vendorClickTrackingService';
import type { Vendor } from '../services/vendorService';
import './VendorCard.css';

interface VendorCardProps {
  vendor: Vendor;
  onStoreDetailsPress?: () => void;
  clickTrackingSource?: string;
  className?: string;
}

/**
 * SEO-optimized vendor card component
 */
export function VendorCard({
  vendor,
  onStoreDetailsPress,
  clickTrackingSource = 'vendors_page',
  className = '',
}: VendorCardProps) {
  // Get vendor initials for placeholder
  const getInitials = (name: string): string => {
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Select the best coupon (affiliate vs scraped)
  const activeCoupon = selectBestCoupon({
    coupon_code: vendor.couponCode || null,
    coupon_description: vendor.couponDescription || null,
    scraped_coupon_code: vendor.scrapedCouponCode || null,
    scraped_coupon_discount: vendor.scrapedCouponDiscount || null,
  });

  const handleCopyCoupon = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      // Could show a toast notification here
      console.log('Coupon code copied:', code);
    }).catch((err) => {
      console.warn('Failed to copy coupon code', err);
    });
  };

  const handleWebsiteClick = () => {
    if (!vendor.websiteUrl) {
      return;
    }

    trackVendorClick({
      vendor_id: vendor.id,
      offer_id: null,
      affiliate_url: vendor.websiteUrl,
      source: clickTrackingSource,
      device_platform: 'web',
      peptide_name: null,
      event_type: 'vendor_click',
    }).catch((err) => {
      if (import.meta.env.DEV) {
        console.error('Failed to track vendor click:', err);
      }
    });
  };

  const websiteLinkText = getVendorWebsiteLinkText(vendor);

  return (
    <article className={`vendor-card ${className}`}>
      <div className="vendor-card-content">
        <header className="vendor-card-header">
          {vendor.logoUrl ? (
            <img
              src={vendor.logoUrl}
              alt={`${vendor.name} logo`}
              className="vendor-card-logo"
              loading="lazy"
            />
          ) : (
            <div className="vendor-card-logo-placeholder" aria-label={`${vendor.name} logo placeholder`}>
              {getInitials(vendor.name)}
            </div>
          )}
          <h3 className="vendor-card-name">{vendor.name}</h3>
        </header>

        {vendor.description && (
          <p className="vendor-card-description">{vendor.description}</p>
        )}

        {vendor.tags && vendor.tags.length > 0 && (
          <div className="vendor-card-tags" role="list">
            {vendor.tags.map((tag, index) => (
              <span key={index} className="vendor-card-tag" role="listitem">
                {tag}
              </span>
            ))}
          </div>
        )}

        {activeCoupon.code && (
          <div className="vendor-card-coupon">
            <p className="vendor-card-coupon-label">
              Exclusive coupon for Peptide Finder users:
            </p>
            <button
              type="button"
              className="vendor-card-coupon-pill"
              onClick={() => handleCopyCoupon(activeCoupon.code!)}
              aria-label={`Copy coupon code ${activeCoupon.code}`}
            >
              <div className="vendor-card-coupon-text">
                <span className="vendor-card-coupon-code">{activeCoupon.code}</span>
                {activeCoupon.description && (
                  <span className="vendor-card-coupon-desc">{activeCoupon.description}</span>
                )}
              </div>
              <span className="vendor-card-coupon-icon" aria-hidden="true">📋</span>
            </button>
          </div>
        )}

        <footer className="vendor-card-footer">
          <div className="vendor-card-footer-buttons">
            {onStoreDetailsPress && (
              <button
                type="button"
                onClick={onStoreDetailsPress}
                className="vendor-card-button vendor-card-button-secondary"
                aria-label={`View store details for ${vendor.name}`}
              >
                Store Details
              </button>
            )}
            {vendor.websiteUrl && (
              <a
                href={vendor.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWebsiteClick}
                className="vendor-card-button"
                aria-label={`Visit ${vendor.name} website`}
              >
                {websiteLinkText}
              </a>
            )}
          </div>
        </footer>
      </div>
    </article>
  );
}
